import {
  Badge,
  Checkbox,
  Input,
  message,
  Modal,
  ModalProps,
  Pagination,
  Table,
  TableColumnsType,
  TableColumnType,
  Tag,
} from 'antd';
import CheckContainer from 'components/CheckContainer';
import StatusButton from 'components/StatusButton';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { uniqBy } from 'lodash-es';
import { useState, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslate } from '@tolgee/react';
import { AdminStore } from 'services/api/admin';
import {
  Container,
  Content,
  StoreSelector,
  Task,
  TaskSelector,
} from 'views/Aide/Scenario/MoveStoreModal';
import { moveStores } from './service';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export interface MoveStoreModalProps extends ModalProps {
  storeList: AdminStore[];
  taskList: Aide.Pause.Task[];
  onUpdate: () => void;
}

function MoveStoreModal(props: MoveStoreModalProps) {
  const { visible, storeList, taskList, onUpdate, ...otherProps } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { configMap: storeNameConfigMap } = useStoreNameConfig();

  const { whiteListConfig: { AIDE_REMOVE_TASK = false } = {} } = useOrgConfigStateValue();

  const [selectedStoreList, setSelectedStoreList] = useState<AdminStore[]>([]);

  const [activeTask, setActiveTask] = useState<Aide.Pause.Task>();

  const tasks = taskList.map((task) => {
    const { id, name, isDefault, status } = task;

    return (
      <CheckContainer
        key={id}
        checked={id === activeTask?.id}
        onChange={(checked) => {
          if (checked) {
            setActiveTask(task);
          }
        }}
      >
        <Task>
          <div>{name}</div>
          {isDefault && <Tag>{t('marketing.默认')}</Tag>}
        </Task>
        <Badge
          status={status === 'VALID' ? 'success' : 'default'}
          text={status === 'VALID' ? t('marketing.启用中') : t('marketing.未启用')}
        />
      </CheckContainer>
    );
  });

  const taskSelector = (
    <TaskSelector>
      <div>
        <h3>{AIDE_REMOVE_TASK ? t('department.选择x', { x: t('policy') }) : t('aide.选择任务')}</h3>
        {tasks}
      </div>
      <footer>{t('marketing.已选 x', { x: activeTask ? activeTask.name : '' })}</footer>
    </TaskSelector>
  );

  const [keywords, setKeywords] = useState('');

  const trimedKeywords = keywords.trim();

  const [filterRegionList, setFilterRegionList] = useState<General.UpperCaseRegion[]>([]);

  const filteredList = useMemo(() => {
    const regionFilteredList =
      filterRegionList.length === 0
        ? storeList || []
        : (storeList || []).filter(({ region }) => filterRegionList.includes(region));

    if (!trimedKeywords) {
      return regionFilteredList;
    }

    return regionFilteredList.filter(({ storeName, alias, id }) => {
      return (
        storeName.toLowerCase().includes(trimedKeywords.toLowerCase()) ||
        alias.toLowerCase().includes(trimedKeywords.toLowerCase()) ||
        `${id}`.includes(trimedKeywords)
      );
    });
  }, [storeList, trimedKeywords, filterRegionList]);

  const regionList = useMemo(() => {
    return (storeList || []).reduce<General.UpperCaseRegion[]>((res, { region }) => {
      if (res.includes(region)) {
        return res;
      }

      res.push(region);

      return res;
    }, []);
  }, [storeList]);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const displayList = useMemo(() => {
    return filteredList.slice((current - 1) * pageSize, current * pageSize);
  }, [filteredList, current, pageSize]);

  const storeChecked = (store: AdminStore) => selectedStoreList.some(({ id }) => id === store.id);

  const currentPageChecked = displayList.every(storeChecked);
  const currentPageIndeterminate = !currentPageChecked && displayList.some(storeChecked);

  const columns: TableColumnsType<AdminStore> = [
    ...(displayList.length > 0
      ? [
          {
            key: 'checked',
            title: (
              <Checkbox
                checked={currentPageChecked}
                indeterminate={currentPageIndeterminate}
                onChange={({ target: { checked } }) => {
                  setSelectedStoreList((pre) => {
                    if (checked) {
                      return uniqBy([...pre, ...displayList], 'id');
                    }

                    return pre.filter(({ id }) => displayList.every((store) => store.id !== id));
                  });
                }}
              />
            ),
            width: 20,
            align: 'center',
            render: (_, store: AdminStore) => {
              return (
                <Checkbox
                  checked={storeChecked(store)}
                  onChange={({ target: { checked } }) => {
                    setSelectedStoreList((pre) => {
                      if (checked) {
                        return uniqBy([...pre, store], 'id');
                      }

                      return pre.filter(({ id }) => id !== store.id);
                    });
                  }}
                />
              );
            },
          } as TableColumnType<AdminStore>,
        ]
      : []),
    {
      key: 'region',
      dataIndex: 'region',
      title: t('aide.站点'),
      width: 150,
      filters: regionList.map((region) => {
        return {
          value: region,
          text: getRegionLabel(region),
        };
      }),
      render: (region: AdminStore['region']) => getRegionLabel(region),
    },
    {
      key: 'storeName',
      title: t('marketing.店铺名称'),
      width: 250,
      render: (_, record: AdminStore) => {
        const { externalType, region, storeName, alias } = record;

        const displayName = storeNameConfigMap?.[externalType]?.[region]
          ? alias || storeName
          : storeName;

        return <Highlighter autoEscape searchWords={[keywords]} textToHighlight={displayName} />;
      },
    },
  ];

  const table = (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={displayList}
      pagination={false}
      onChange={(_, { region }) => setFilterRegionList((region as any) || [])}
    />
  );

  const footer = storeList.length > 0 && (
    <footer>
      <div>
        <Checkbox
          checked={storeList.length === selectedStoreList.length}
          indeterminate={
            storeList.length > selectedStoreList.length && selectedStoreList.length > 0
          }
          onChange={(e) => setSelectedStoreList(e.target.checked ? storeList : [])}
        >
          {t('marketing.全选')}
        </Checkbox>
        <div>{t('marketing.已选 x', { x: selectedStoreList.length })}</div>
      </div>
      <Pagination
        size="small"
        current={current}
        pageSize={pageSize}
        total={filteredList.length}
        onChange={(current, pageSize) => {
          setCurrent(current);
          setPageSize(pageSize);
        }}
        showSizeChanger={false}
      />
    </footer>
  );

  const storeSelector = (
    <StoreSelector>
      <div>
        <h3>
          {`${t('marketing.店铺')}(${(storeList || []).length})`}
          <Input.Search
            allowClear
            placeholder={t('aide.请输入店铺名称/店铺别名/官方ID')}
            onSearch={(keywords) => {
              setKeywords(keywords);
              setCurrent(1);
            }}
          />
        </h3>
        {table}
      </div>
      {footer}
    </StoreSelector>
  );

  const content = (
    <Content>
      {storeSelector}
      {taskSelector}
    </Content>
  );

  const reset = () => {
    setActiveTask(undefined);
    setSelectedStoreList([]);
  };

  const saveButton = (
    <StatusButton
      disabled={selectedStoreList.length === 0 || !activeTask}
      type="primary"
      onClick={(e) => {
        return !activeTask
          ? Promise.resolve()
          : moveStores({
              storeIds: selectedStoreList.map(({ id }) => id),
              taskId: activeTask.id,
              scene: 'PAUSE_AUTO_REPLY',
            })
              .then(() => {
                otherProps.onCancel?.(e);

                message.success(t('operation.saveSucceed'));

                onUpdate();

                reset();
              })
              .catch(() => {
                message.error(t('operation.saveFailed'));
              });
      }}
    >
      {t('aide.保存并生效')}
    </StatusButton>
  );

  return (
    <Modal
      title={AIDE_REMOVE_TASK ? t('unset_store_of_policy') : t('aide.未加入任务的店铺')}
      visible={visible}
      width={800}
      maskClosable={false}
      footer={saveButton}
      {...otherProps}
    >
      <Container>{content}</Container>
    </Modal>
  );
}

export default MoveStoreModal;
